import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';

import { updateWinnerAnimationFlag } from 'api/client';
import { prizeTypes, getPrizeTypeLabel } from 'config/promoPrizeTypes';
import parseAnimationSettings from 'utils/parseAnimationSettings';
import useClientHistory, { RESULT, PRIZE } from 'client/hooks/useClientHistory';
import { useAnimationPathPrizeList } from 'client/hooks/useLotteryPrizeList';
import { gtmPushPrize } from 'client/utils/tracking';
import PopupWinnerGet from 'client/components/Prize/PopupWinnerGet';
import useGetLotteryAndPrizeAndPromoData from 'hooks/useGetLotteryAndPrizeAndPromoData';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';

export default function AnimationContainer({
    match: {
        params: { entriesUUID, prizeUUID }
    },
    children
}) {
    const history = useClientHistory();
    const errHandler = useClientErrorHandler();
    const { id: promoId } = useSelector(s => s.promo);
    const { loginId } = useSelector(s => s.userLoginState);
    const { isMgmShare, isMgmSelf } = useSelector(s => s.flags);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { uuid: lotteryUUID, AnimationSettings } = useSelector(s => s.flags.animatingLotteries);
    const openWinnerPopupHandler = useCallback(() => setIsModalOpen(true), []);
    const closeWinnerPopupHandler = useCallback(() => setIsModalOpen(false), []);

    const parsedAnimationSettings = useMemo(() => parseAnimationSettings(AnimationSettings || {}), [
        AnimationSettings
    ]);
    const lotteryParticipantPrizeList = useAnimationPathPrizeList(
        lotteryUUID,
        isMgmShare,
        isMgmSelf
    );
    const winnerInfo = lotteryParticipantPrizeList
        ? lotteryParticipantPrizeList.find(
              lotteryParticipantPrize => lotteryParticipantPrize.uuid === prizeUUID
          )
        : {};
    const { settings: { imageUrl } = {}, name: prizeText, type: prizeType } = winnerInfo;
    const { getPromoByPrizeAndEntry } = useGetLotteryAndPrizeAndPromoData();
    const { uuid: currentPromoUUID } = getPromoByPrizeAndEntry(prizeUUID, entriesUUID);

    // TODO： change new animation API
    const updateAnimationFlag = useCallback(() => {
        const update = async () => {
            try {
                if (prizeUUID) {
                    await updateWinnerAnimationFlag(currentPromoUUID, entriesUUID, prizeUUID);
                    gtmPushPrize({
                        prizeName: prizeText,
                        prizeType: getPrizeTypeLabel(prizeType),
                        loginId,
                        promoId
                    });
                }
            } catch (error) {
                console.log('Failed to update winner animation flag:', error);
                errHandler({ err: error });
            }
        };

        update();
    }, [
        prizeUUID,
        currentPromoUUID,
        entriesUUID,
        prizeText,
        prizeType,
        loginId,
        promoId,
        errHandler
    ]);

    const props = useMemo(() => {
        return {
            lotteryParticipantPrizeList,
            animationSettings: parsedAnimationSettings,
            updateAnimationFlag,
            openWinnerPopupHandler,
            winnerInfo: {
                prizeUUID,
                prizeImageUrl: imageUrl,
                prizeText,
                prizeType
            }
        };
    }, [
        lotteryParticipantPrizeList,
        parsedAnimationSettings,
        updateAnimationFlag,
        openWinnerPopupHandler,
        prizeUUID,
        imageUrl,
        prizeText,
        prizeType
    ]);

    const isThanksPrize = props.winnerInfo.prizeType === prizeTypes.THANKYOU;

    return (
        lotteryParticipantPrizeList && (
            <>
                {children({ ...props })}
                <PopupWinnerGet
                    title={
                        isThanksPrize
                            ? i18n.t('label.keep_up_the_work')
                            : i18n.t('label.congratulation')
                    }
                    label={props.winnerInfo.prizeText}
                    imgSrc={props.winnerInfo.prizeImageUrl}
                    isOpen={isModalOpen}
                    onConfirm={() => {
                        closeWinnerPopupHandler();
                        if (isThanksPrize) {
                            history.replace(RESULT);
                        } else {
                            history.replace(PRIZE, {
                                prizeUUID: prizeUUID,
                                entriesUUID: entriesUUID
                            });
                        }
                    }}
                    btnText={isThanksPrize ? i18n.t('btn.close') : i18n.t('button.receive_prize')}
                />
            </>
        )
    );
}
